import React from 'react';
import Text from '../../components/Text.js';
import TextInput from '../../components/TextFields/TextInput.js';
import DateInput from '../../components/TextFields/DateInput.js';
import NumberInput from '../../components/TextFields/NumberInput.js';
import Grid from '@mui/material/Grid';
import * as Services from '../../services/index.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import * as localizationMaps from '../../localization/maps.js';

/**
 * 
 * @param {*} value 
 * @returns 
 */
function isDefined(value) {
  return value !== undefined && value !== null;
}

/**
 * Display beneficiary change data
 * @param data
 * @param errors
 * @param handleChange
 * @param isDisabled
 * @param useNewData
 */
function BeneficiaryDetails({
  data,
  errors,
  handleChange,
  isDisabled,
  useNewData = true
}) {
  // select old or new data to display
  const sectionData = data[useNewData ? 'newData' : 'oldData'];
  return /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    columnSpacing: 2
  }, isDefined(sectionData.firstname) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: sectionData.firstname,
    onChange: event => handleChange('firstname', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.firstname,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.lastname) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: sectionData.lastname,
    onChange: event => handleChange('lastname', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.lastname,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.relationship) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.relationship),
    value: localizationMaps.fromEnglish('relationships', sectionData.relationship),
    onChange: event => handleChange('relationship', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.relationship,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled,
    select: true,
    selectOptions: Services.selectFieldOptions.optionsWithSpouse()
  })), isDefined(sectionData.birthdate) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.birthdate),
    value: sectionData.birthdate,
    onChange: event => handleChange('birthdate', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.birthdate,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.type) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.type),
    value: localizationMaps.fromEnglish('beneficiariesOptions', sectionData.type),
    onChange: event => handleChange('type', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.type,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled,
    select: true,
    selectOptions: sectionData.type?.toUpperCase() === 'CONTINGENT' ? [i18n.t(tokens.dropdowns.beneficiaries.contingent)] : Services.selectFieldOptions.beneficiariesOptions()
  })), isDefined(sectionData.shares) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.changeRequests.percentOfBenefits),
    suffix: "%",
    value: sectionData.shares,
    onChange: event => handleChange('shares', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.shares,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), Array.isArray(sectionData?.contingent) && sectionData.type?.toUpperCase() === 'CONTINGENT' && sectionData.contingent.map((item, index) => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    key: index
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: `Remaining Share Percentage to ${item.firstname}`,
    suffix: "%",
    value: item.shares,
    onChange: event => handleChange('shares', event.target.value, 'contingent', index),
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  }))), Array.isArray(sectionData?.primary) && ['REVOCABLE', 'IRREVOCABLE'].includes(sectionData.type?.toUpperCase()) && sectionData.primary.map((item, index) => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    key: index
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: `Remaining Share Percentage to ${item.firstname}`,
    suffix: "%",
    value: item.shares,
    onChange: event => handleChange('shares', event.target.value, 'primary', index),
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  }))));
}

/**
 * @param data
 * @param errors
 * @param accessData
 * @param handleChange
 */
function BeneficiaryChange({
  data,
  errors,
  accessData,
  handleChange
}) {
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    sectionHeader: {
      marginTop: '25px'
    },
    subSectionHeader: {
      marginTop: '12px'
    }
  };

  /**
   * Always disabled for admin/members, disabled for master users if not in 'Pending', 'Incomplete', or 'Suspended' status or no write access
   */
  const isAgentCanReview = accessData.agentWriteAccess && ['PENDING AGENT REVIEW', 'INCOMPLETE', 'SUSPENDED'].includes(data.status?.toUpperCase());
  const isDisabled = !accessData.isAgent || !isAgentCanReview;
  return /*#__PURE__*/React.createElement("div", {
    style: styles.container
  }, ['Add Irrevocable Beneficiary', 'Add Revocable Beneficiary'].includes(data.type) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: styles.sectionHeader
  }, i18n.t(tokens.labels.changeRequests.addBeneficiary)), /*#__PURE__*/React.createElement(BeneficiaryDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  })), ['Edit Irrevocable Beneficiary', 'Edit Revocable Beneficiary'].includes(data.type) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: styles.sectionHeader
  }, i18n.t(tokens.labels.changeRequests.editBeneficiary)), /*#__PURE__*/React.createElement(Text, {
    body: true,
    light: true,
    customStyle: styles.subSectionHeader
  }, i18n.t(tokens.labels.changeRequests.oldDetails)), /*#__PURE__*/React.createElement(BeneficiaryDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  }), /*#__PURE__*/React.createElement(Text, {
    body: true,
    light: true,
    customStyle: styles.subSectionHeader
  }, i18n.t(tokens.labels.changeRequests.newDetails)), /*#__PURE__*/React.createElement(BeneficiaryDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  })), ['Remove Irrevocable Beneficiary', 'Remove Revocable Beneficiary'].includes(data.type) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: styles.sectionHeader
  }, i18n.t(tokens.labels.changeRequests.removeBeneficiary)), /*#__PURE__*/React.createElement(BeneficiaryDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  })));
}
export default BeneficiaryChange;